import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LogOut, Menu, Settings, User } from 'lucide-react';
import LanguageSwitch from '@/components/ui/LanguageSwitch';
import { useAuth } from '@/contexts/AuthContext';
import { useEmbeddingSync } from '@/contexts/EmbeddingSyncContext';
import csdcLogo from '@/assets/csdc-logo-navbar.svg';

interface HeaderProps {
  onToggleSidebar: () => void;
}

const useCrumbs = (): string[] => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();

  return useMemo(() => {
    const path = location.pathname;
    const root = t('app.title');
    if (path === '/') return [root, t('nav.dashboard')];
    if (path.startsWith('/credentials')) return [root, t('credentials.title')];
    if (path.startsWith('/servers')) return [root, t('nav.servers')];
    if (path.startsWith('/groups')) return [root, t('nav.groups')];
    if (path.startsWith('/prompts')) return [root, t('nav.prompts')];
    if (path.startsWith('/resources')) return [root, t('nav.resources')];
    if (path.startsWith('/users')) return [root, t('nav.users')];
    if (path.startsWith('/market')) {
      const serverName = (params as { serverName?: string }).serverName;
      const crumbs = [root, t('nav.market')];
      if (serverName) crumbs.push(serverName);
      return crumbs;
    }
    if (path.startsWith('/logs')) return [root, t('nav.logs')];
    if (path.startsWith('/activity')) return [root, t('nav.activity')];
    if (path.startsWith('/settings')) return [root, t('nav.settings')];
    return [root];
  }, [location.pathname, params, t]);
};

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const { t } = useTranslation();
  const { auth, logout } = useAuth();
  const { activeSyncs } = useEmbeddingSync();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const crumbs = useCrumbs();
  const username = auth.user?.username || 'Admin';
  const userEmail = username.includes('@') ? username : `${username}@csdc.dev`;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate('/login');
  };

  const handleSettingsClick = () => {
    setIsUserMenuOpen(false);
    navigate('/settings');
  };

  return (
    <header className="hub-topbar shrink-0">
      <button
        onClick={onToggleSidebar}
        className="hub-icon-btn text-[#111111]"
        aria-label={t('app.toggleSidebar')}
      >
        <Menu size={18} />
      </button>

      <div className="flex items-center gap-2.5 shrink-0">
        <img src={csdcLogo} alt="CSDC" className="h-10 w-auto object-contain" />
      </div>

      <div className="hub-crumb flex items-center min-w-0">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="sep">/</span>}
            {i === crumbs.length - 1 ? (
              <b className="truncate">{c}</b>
            ) : (
              <span className="truncate">{c}</span>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="flex-1 flex justify-center px-2 min-w-0">
        {activeSyncs.length > 0 && (
          <div className="flex max-w-full flex-wrap justify-center gap-2">
            {activeSyncs.map((activeSync) => (
              <div
                key={activeSync.serverName}
                className="flex min-w-0 w-56 max-w-full flex-col px-3 py-1.5 text-xs rounded-[7px] bg-[#FFF9C4] border border-black/[0.06]"
                title={t('app.embeddingSyncProgressAriaLabel', {
                  serverName: activeSync.serverName,
                  current: activeSync.current,
                  total: activeSync.total,
                })}
              >
                <span className="truncate hub-mono text-[#555555]">
                  {t('app.embeddingSyncProgress', { serverName: activeSync.serverName })}
                </span>
                <div className="mt-1 flex items-center gap-2">
                  <progress
                    className="h-1.5 flex-1"
                    value={activeSync.current}
                    max={activeSync.total}
                    aria-label={t('app.embeddingSyncProgressAriaLabel', {
                      serverName: activeSync.serverName,
                      current: activeSync.current,
                      total: activeSync.total,
                    })}
                  />
                  <span className="shrink-0 hub-mono hub-num text-[#777777] text-[11px]">
                    {activeSync.current}/{activeSync.total}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-0.5 shrink-0">
        {/* <a
          href="https://github.com/samanhappy/mcphub"
          target="_blank"
          rel="noopener noreferrer"
          className="hub-icon-btn"
          aria-label="GitHub Repository"
        >
          <Github size={18} color="#6B7280" />
        </a>
        <a
          href="https://docs.mcphub.app"
          target="_blank"
          rel="noopener noreferrer"
          className="hub-icon-btn"
          aria-label="Documentation"
        >
          <BookOpen size={18} color="#6B7280" />
        </a> */}
        <LanguageSwitch />

        <div ref={userMenuRef} className="relative ml-2 shrink-0">
          <button
            type="button"
            onClick={() => setIsUserMenuOpen((open) => !open)}
            className="flex items-center gap-2 rounded-[10px] p-1 transition-colors hover:bg-black/[0.04]"
            aria-haspopup="menu"
            aria-expanded={isUserMenuOpen}
            aria-label={t('app.profile')}
          >
            <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#F3E9A8]">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8A8A8A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
              </svg>
            </span>
            <span className="font-bold text-[14px] tracking-tight hidden sm:inline text-[#111111]">
              {username}
            </span>
          </button>

          {isUserMenuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-50 mt-[9px] w-[264px] rounded-[14px] border border-[#D2D2D7] bg-white p-2 shadow-[0_12px_30px_rgba(0,0,0,0.15)]"
            >
              <span className="absolute right-[30px] top-[-9px] h-0 w-0 border-x-8 border-b-[9px] border-x-transparent border-b-white" />
              <div className="mb-3 flex h-[72px] items-center gap-3 rounded-[11px] bg-[#F7F7F8] px-3">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#FFE600] text-[#1D1D1F]">
                  <User className="size-5" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-bold text-[#1D1D1F]">{username}</span>
                  <span className="mt-1 block truncate text-[11px] text-[#7A7A80]">{userEmail}</span>
                </span>
              </div>

              <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#8A8A90]">
                {t('profile.account')}
              </div>

              <button
                type="button"
                role="menuitem"
                onClick={handleSettingsClick}
                className="flex h-10 w-full items-center gap-3 rounded-[9px] bg-[#FFFBE0] px-3 text-left text-[13px] font-medium text-[#1D1D1F] transition-colors hover:bg-[#FFF7B8]"
              >
                <Settings className="size-4 text-[#6E6E73]" />
                {t('profile.accountSettings')}
              </button>

              <div className="mx-2 my-2 h-px bg-[#ECECEE]" />

              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex h-8 w-full items-center gap-3 rounded-[8px] px-3 text-left text-[13px] font-semibold text-[#D92D20] transition-colors hover:bg-[#FEF3F2]"
              >
                <LogOut className="size-4 text-[#D92D20]" />
                {t('app.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
